import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {
	SyntheticEvent,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { LoadingIndicator } from '@/components/LoadingIndicator/LoadingIndicator';
import { Modal } from '@/components/Modal/Modal';
import {
	NavigationTabs,
	NavigationTab
} from '@/components/NavigationTabs/NavigationTabs';
import { Country, DcrType, EntityType } from '@/core/graphql/generated/types';
import { paths } from '@/core/router/paths';
import { getDcrNavSections } from '@/core/store/dcrNavigation/dcrNavigationSelectors';
import { useSetDcrFormSearchTerm } from '@/core/store/dcrs/dcrSelectors';
import {
	HcoProfile,
	useHcoData
} from '@/pages/HcoProfilePage/_hooks/useHcoData';
import { useHcpData } from '@/pages/HcpProfilePage/_hooks/useHcpData';
import { asyncScrollToElement } from '@/utils/navigation/asyncScrollToElement';
import { useScrollSpy } from '@/utils/navigation/useScrollSpy';
import { useBreakpoint } from '@/utils/useBreakpoint';

import { HcoDcrSectionFields } from '../_constants/HcoDcrSectionsFields';
import { HcpDcrSectionFields } from '../_constants/HcpDcrSectionsFields';
import { DcrContext } from '../_context/DcrContext';
import { useDeleteDcrAttachmentMutation } from '../_graphql/mutation/DeleteDcrAttachment.hook';
import { usePostDcrMutation } from '../_graphql/mutation/PostDCR.hook';
import { useGetAllCountriesQuery } from '../_graphql/query/GetAllCountries.hook';
import { useGetPreferredCountryQuery } from '../_graphql/query/GetPreferredCountry.hook';
import { mapHcpToNetworkApiFieldNames } from '../_hooks/mapHcpToNetworkApiFieldNames';
import { useAttachmentUpload } from '../_hooks/useAttachmentUpload';
import { useDcrSetup } from '../_hooks/useDcrSetup';
import { usePicklistValues } from '../_hooks/usePicklistValues';
import {
	HcoDcrSchema,
	HcoDcrValuesIn,
	HcoDcrValuesOut
} from '../_schemas/hcoSchema';
import {
	HcpDcrSchema,
	type HcpDcrValuesIn,
	type HcpDcrValuesOut
} from '../_schemas/hcpSchema';
import type { DcrAttachment } from '../_types/attachment';
import { getDirtyValuesHybrid } from '../_utils/getFormDirtyValuesHybrid';
import {
	HCO_EMPTY_DEFAULTS_IN,
	mapHcoToFormDefaultsIn
} from '../_utils/mapHcoToFormDefaultsIn';
import {
	HCP_EMPTY_DEFAULTS_IN,
	mapHcpToFormDefaultsIn
} from '../_utils/mapHcpToFormDefaultsIn';
import { mapHcoToNetworkApiFieldNames } from '../_utils/networkApiMappers/mapHcoToNetworkApiFieldNames';
import { DcrSuccessModalContent } from '../DcrSuccessModalContent/DcrSuccessModalContent';
import { DiscardChangesWarningModal } from '../DiscardChangesWarningModal/DiscardChangesWarningModal';
import { PickCountryModal } from '../PickCountryModal/PickCountryModal';
import { PickCountryModalActions } from '../PickCountryModal/PickCountryModal/PickCountryModalActions';
import { ReviewModalContent } from '../ReviewModalContent/ReviewModalContent';
import { ReviewModalDialogActions } from '../ReviewModalContent/ReviewModalDialogActions/ReviewModalDialogActions';

import { hcpDataChangeRequestStyles as sx } from './DataChangeRequest.styles';
import { DcrModalDialogActions } from './DcrModalDialogActions/DcrModalDialogActions';
import { FormSearch } from './FormSearch/FormSearch';
import { HcoDcrModalContent } from './HcoDcrModalContent/HcoDcrModalContent';
import { HcpDcrModalContent } from './HcpDcrModalContent/HcpDcrModalContent';
import { NoFormFields } from './NoFormFields/NoFormFields';

type Props = {
	open: boolean;
	formType?: DcrType;
	entityType: EntityType;
	onClose: () => void;
	onLoaded: () => void;
};

export type FormValuesIn = HcpDcrValuesIn | HcoDcrValuesIn;
export type FormValuesOut = HcpDcrValuesOut | HcoDcrValuesOut;

type ModalStep = 'PICK_COUNTRY' | 'FORM' | 'REVIEW' | 'SUCCESS';

const DCR_AFFILIATIONS_PAGE_SIZE = 50;

export const DataChangeRequest = ({
	open,
	formType = DcrType.Change,
	entityType,
	onClose: onCloseCallback,
	onLoaded
}: Props) => {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const scrollRef = useRef<HTMLDivElement>(null);
	const openSessionInitializedRef = useRef(false);
	const isMd = useBreakpoint('md', 'down');
	const setQuery = useSetDcrFormSearchTerm();

	const { hcp, loading: hcpLoading } = useHcpData({
		suppressErrors: true,
		affiliationsPageSize: DCR_AFFILIATIONS_PAGE_SIZE,
		enabled: open
	});
	const { hco, loading: hcoLoading } = useHcoData({
		suppressErrors: true,
		affiliationsPageSize: DCR_AFFILIATIONS_PAGE_SIZE,
		enabled: open
	});

	const dcrType = hcp || hco ? DcrType.Change : DcrType.Add;
	const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);
	const [newCountryId, setNewCountryId] = useState<string | undefined>(
		undefined
	);
	const [currentTab, setCurrentTab] = useState(0);
	const [isNavigating, setIsNavigating] = useState(false);
	const availableTabs = getDcrNavSections();
	const [isSimpleView, setIsSimpleView] = useState(dcrType === DcrType.Add);
	const [formGeneration, setFormGeneration] = useState(0);

	const activeCountryId =
		(entityType === EntityType.Hcp ? hcp?.countryId : hco?.countryId) ??
		newCountryId;

	const [currentStep, setCurrentStep] = useState<ModalStep>(
		(entityType === EntityType.Hcp ? hcp : hco) ? 'FORM' : 'PICK_COUNTRY'
	);

	const isAddFlow = !(entityType === EntityType.Hcp ? hcp : hco);

	const { data: allCountries, loading: countriesLoading } =
		useGetAllCountriesQuery({
			skip: !open || !isAddFlow
		});

	const { data: preferredCountryData, loading: preferredCountryLoading } =
		useGetPreferredCountryQuery({
			skip: !open || !isAddFlow
		});

	const countryOptions = useMemo(
		() => (allCountries?.countries ?? []) as Country[],
		[allCountries?.countries]
	);

	const preferredCountryId = useMemo(() => {
		const results =
			preferredCountryData?.preferredCountryDcrs.__typename === 'DCRResults'
				? preferredCountryData.preferredCountryDcrs.results
				: [];
		return results[0]?.country?.id;
	}, [preferredCountryData]);

	const allowMultipleEmails = activeCountryId === 'us';

	const schema = useMemo(
		() =>
			entityType === EntityType.Hcp
				? HcpDcrSchema(t, allowMultipleEmails)
				: HcoDcrSchema(t),
		[entityType, t, allowMultipleEmails]
	);

	const [deleteDcrAttachmentMutation] = useDeleteDcrAttachmentMutation();

	const [postDcr, { data: postDcrData, reset: resetMutation }] =
		usePostDcrMutation({
			refetchQueries: ['getDcrs'],
			update(cache) {
				// Evict so MyDCRs page picks up the new DCR on next mount when the
				// query isn't active at submit time (e.g. submitting from a profile page).
				cache.evict({ id: 'ROOT_QUERY', fieldName: 'getDcrs' });
				cache.gc();
			}
		});

	const methods = useForm<FormValuesIn, unknown, FormValuesOut>({
		resolver: zodResolver(schema),
		defaultValues:
			entityType === EntityType.Hcp
				? HCP_EMPTY_DEFAULTS_IN
				: HCO_EMPTY_DEFAULTS_IN,
		mode: 'onChange',
		shouldUnregister: false,
		criteriaMode: 'all'
	});

	const { isValid, dirtyFields, errors } = methods.formState;

	const selectedNewCountryId = useWatch({
		control: methods.control,
		name: 'newEntityCountryId'
	});

	const fetchCountryId = selectedNewCountryId || activeCountryId;

	const { unavailableFields, loading: setupLoading } = useDcrSetup({
		isModalOpen: open,
		countryId: fetchCountryId
	});

	const { picklists, loading: picklistsLoading } = usePicklistValues({
		isModalOpen: open,
		countryId: fetchCountryId
	});

	const currentValues = useWatch({
		control: methods.control
	});

	const hybridChanges = useMemo(
		() =>
			getDirtyValuesHybrid(
				dirtyFields,
				currentValues as Partial<FormValuesIn>,
				methods.formState.defaultValues
			),
		[dirtyFields, currentValues, methods.formState.defaultValues]
	);

	useEffect(() => {
		if (postDcrData?.postDcr?.__typename === 'DCR') {
			setCurrentStep('SUCCESS');
		}
	}, [postDcrData]);

	useEffect(() => {
		setIsSimpleView(dcrType === DcrType.Add);
	}, [dcrType]);

	useEffect(() => {
		if (scrollRef.current) {
			scrollRef.current.scrollTop = 0;
		}
	}, [currentStep]);

	const resetForm = useCallback(() => {
		if (entityType === EntityType.Hcp) {
			if (hcp) {
				methods.reset(mapHcpToFormDefaultsIn(hcp));
			} else {
				methods.reset(HCP_EMPTY_DEFAULTS_IN);
			}
		} else if (entityType === EntityType.Hco) {
			if (hco) {
				methods.reset(mapHcoToFormDefaultsIn(hco as HcoProfile));
			} else {
				methods.reset(HCO_EMPTY_DEFAULTS_IN);
			}
		}
		setFormGeneration(generation => generation + 1);
	}, [entityType, hcp, hco, methods]);

	// Auto-switch to FORM if HCP data loads "late"
	useEffect(() => {
		if ((hcp || hco) && currentStep === 'PICK_COUNTRY') {
			setCurrentStep('FORM');
			// Ensure we reset the form to the HCP data
			resetForm();
		}
	}, [entityType, hcp, hco, currentStep, methods, resetForm]);

	useEffect(() => {
		if (scrollRef.current) {
			scrollRef.current.scrollTop = 0;
		}
	}, [currentStep]);

	// Opening / Resetting
	useLayoutEffect(() => {
		if (!open) {
			openSessionInitializedRef.current = false;
			return;
		}

		setCurrentTab(0);
		if (!hcpLoading && !hcoLoading) {
			if (openSessionInitializedRef.current && currentStep !== 'PICK_COUNTRY') {
				return;
			}
			openSessionInitializedRef.current = true;

			resetMutation();
			setNewCountryId(undefined);

			// Initialize defaults based on what we have NOW
			resetForm();
			setQuery('');
			if (hcp || hco) {
				setCurrentStep('FORM');
			} else {
				// Only go to pick country if we are genuinely missing HCP data (Create Mode) and cannot infer country
				const singleCountryId =
					countryOptions.length === 1 ? countryOptions[0].id : undefined;

				if (singleCountryId) {
					setNewCountryId(singleCountryId);
					setCurrentStep('FORM');
				} else {
					if (preferredCountryId) {
						setNewCountryId(preferredCountryId);
						methods.setValue('newEntityCountryId', preferredCountryId, {
							shouldValidate: true
						});
					}
					setCurrentStep('PICK_COUNTRY');
				}
				setIsSimpleView(true);
			}
		}
	}, [
		open,
		hcp,
		hco,
		hcpLoading,
		hcoLoading,
		methods,
		resetMutation,
		countryOptions,
		preferredCountryId,
		resetForm,
		entityType,
		setQuery,
		currentStep
	]);

	const handleGoToReview = () => setCurrentStep('REVIEW');
	const handleBackToForm = () => {
		methods.unregister('isThirdPartyDataConfirmed');

		setCurrentStep('FORM');
	};

	const handleShowUnsavedDialog = () => {
		// Note: Blur the currently focused close/action button before mounting
		// the nested confirm dialog - otherwise MUI marks the underlying DCR
		// dialog aria-hidden while that button still retains focus.
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
		setShowUnsavedConfirm(true);
	};
	const handleHideUnsavedDialog = () => setShowUnsavedConfirm(false);

	const handleSubmit = (values: HcpDcrValuesOut | HcoDcrValuesOut) => {
		const mappedEntity =
			entityType === EntityType.Hcp
				? mapHcpToNetworkApiFieldNames({
						payload: hybridChanges,
						vid_V: hcp?.vid_V ?? undefined,
						countryId: activeCountryId,
						picklists,
						unavailableFields,
						hcp: hcp ?? null
					})
				: mapHcoToNetworkApiFieldNames({
						payload: hybridChanges,
						vid_V: hco?.vid_V ?? undefined,
						picklists,
						countryId: activeCountryId,
						unavailableFields,
						hco: hco ?? null
					});

		const note =
			values?.notes && values.notes.length > 0 ? values.notes : undefined;

		const attachmentIds = (values?.attachments ?? [])
			.filter(att => att.status === 'success' && !!att.remoteId)
			.map(att => att.remoteId as string);

		postDcr({
			variables: {
				payload: {
					note,
					attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
					entity: {
						...(entityType === EntityType.Hcp
							? { hcp: mappedEntity }
							: { hco: mappedEntity })
					}
				}
			}
		});
	};

	const discardOrphanAttachments = useCallback(() => {
		const formAttachments = methods.getValues().attachments ?? [];

		formAttachments.forEach(att => {
			if (att.remoteId) {
				deleteDcrAttachmentMutation({
					variables: { attachmentId: att.remoteId }
				}).catch(() => {});
			}
		});
	}, [methods, deleteDcrAttachmentMutation]);

	const discardAndClose = useCallback(() => {
		if (currentStep !== 'SUCCESS') discardOrphanAttachments();
		onCloseCallback();
	}, [currentStep, discardOrphanAttachments, onCloseCallback]);

	const handleClose = (
		_event?: object,
		_?: 'backdropClick' | 'escapeKeyDown'
	) => {
		const isIgnoredChange =
			hybridChanges.newEntityCountryId &&
			Object.keys(hybridChanges).length === 1;

		// Prevent accidental close - clicking outside modal boundaries
		if (hasRealChanges && !isIgnoredChange && currentStep !== 'SUCCESS') {
			handleShowUnsavedDialog();
			return;
		}

		discardAndClose();
	};

	const pickCountry = useCallback(
		(countryId: string) => {
			setNewCountryId(countryId);
			methods.setValue('newEntityCountryId', countryId, {
				shouldValidate: true
			});
			setCurrentStep('FORM');
		},
		[methods]
	);

	const handleCountryPicked = useCallback(() => {
		if (selectedNewCountryId) {
			pickCountry(selectedNewCountryId);
		}
	}, [selectedNewCountryId, pickCountry]);

	const hasRealChanges = useMemo(
		() => Object.keys(hybridChanges).length > 0,
		[hybridChanges]
	);

	const isFormDirty = useMemo(() => {
		if (!hasRealChanges) return false;

		const supplementaryKeys = ['notes', 'attachments'];
		const meaningfulKeys = Object.keys(hybridChanges).filter(
			k => !supplementaryKeys.includes(k)
		);

		return meaningfulKeys.length > 0;
	}, [hasRealChanges, hybridChanges]);

	const attachmentSessionId = useMemo(
		() => (open ? window.crypto.randomUUID() : ''),
		[open]
	);

	const attachments =
		(currentValues as Partial<FormValuesIn>).attachments ?? [];

	const handleAttachmentsChange = useCallback(
		(next: DcrAttachment[]) => {
			methods.setValue('attachments', next, {
				shouldDirty: true,
				shouldValidate: true
			});
		},
		[methods]
	);

	const { addFiles, removeAttachment } = useAttachmentUpload({
		attachments,
		onChange: handleAttachmentsChange,
		sessionId: attachmentSessionId
	});

	const contextValue = useMemo(
		() => ({
			entityCountryId: activeCountryId,
			unavailableFields,
			picklists,
			isSimpleView,
			entityType,
			attachmentSessionId
		}),
		[
			activeCountryId,
			unavailableFields,
			picklists,
			isSimpleView,
			entityType,
			attachmentSessionId
		]
	);

	const loading = useMemo(
		() =>
			hcpLoading ||
			hcoLoading ||
			countriesLoading ||
			preferredCountryLoading ||
			(currentStep !== 'PICK_COUNTRY' && (picklistsLoading || setupLoading)),
		[
			countriesLoading,
			currentStep,
			hcoLoading,
			hcpLoading,
			picklistsLoading,
			preferredCountryLoading,
			setupLoading
		]
	);

	useEffect(() => {
		if (!loading) {
			onLoaded();
		}
	}, [loading, onLoaded]);

	useEffect(() => {
		if (open && !loading && (hcp || hco)) {
			methods.trigger();
		}
	}, [open, loading, hcp, hco, methods]);

	const dirtySections = useMemo(
		() =>
			Object.entries(
				entityType === EntityType.Hcp
					? HcpDcrSectionFields
					: HcoDcrSectionFields
			).reduce((acc, [section, fields]) => {
				if (fields.some(f => f in hybridChanges)) {
					// Note (Ben): toLowerCase works here for now - however, if we add a section with whitespace we will need proper mapping between section ID and section name
					acc.push(section.toLowerCase());
				}

				return acc;
			}, [] as string[]),
		[entityType, hybridChanges]
	);

	const errorSections = useMemo(
		() =>
			Object.entries(
				entityType === EntityType.Hcp
					? HcpDcrSectionFields
					: HcoDcrSectionFields
			).reduce((acc, [section, fields]) => {
				if (fields.some(f => !!errors[f as keyof FormValuesIn])) {
					// Note (Ben): toLowerCase works here for now - however, if we add a section with whitespace we will need proper mapping between section ID and section name
					acc.push(section.toLowerCase());
				}
				return acc;
			}, [] as string[]),
		// Note (Ben): errors is a proxy object and therefore cannot trigger the useMemo. Using formState directly will trigger it, however, will retrigger on any change to formState, not just errors, causing issues with the useScrollSpy.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[entityType, JSON.stringify(errors)]
	);

	const tabs: NavigationTab[] = useMemo(
		() =>
			availableTabs.map(t => ({
				...t,
				customSx: {
					color: errorSections.includes(t.id)
						? 'var(--mui-palette-error-main)'
						: dirtySections.includes(t.id)
							? 'var(--mui-palette-success-light)'
							: undefined
				}
			})),
		[availableTabs, errorSections, dirtySections]
	);

	const onScrollTabChange = useCallback(
		(id: string) => {
			if (id) {
				const tabIndex = tabs.findIndex(t => t.id === id.split('-')[2]);

				if (tabIndex !== currentTab) {
					setCurrentTab(tabIndex === -1 ? 0 : tabIndex);
				}
			}
		},
		[currentTab, tabs]
	);

	const ids = useMemo(() => tabs.map(t => `dcr-form-${t.id}`), [tabs]);

	useScrollSpy(ids, onScrollTabChange, scrollRef, {
		offset: 0,
		enabled: !isNavigating
	});

	const onTabChange = (_: SyntheticEvent, index: number) => {
		setCurrentTab(index);
		navigateToSection(`dcr-form-${tabs[index].id}`);
	};

	const navigateToSection = async (id: string) => {
		if (scrollRef.current) {
			setIsNavigating(true);
			await asyncScrollToElement(id, scrollRef.current);
			setIsNavigating(false);
		}
	};

	const allTabsHidden = useMemo(() => tabs.every(t => t.hidden), [tabs]);

	if (
		(loading ||
			(countryOptions.length === 1 && currentStep === 'PICK_COUNTRY')) &&
		formType === DcrType.Change
	)
		return null;

	const isFullHeight = !isSimpleView && currentStep === 'FORM' && !loading;

	const titleNode = (
		<Box sx={sx.title}>
			<Box sx={sx.mainHeader}>
				<Typography sx={sx.textTitle} variant='h2'>
					{dcrType === DcrType.Add
						? entityType === EntityType.Hcp
							? t('Add HCP')
							: t('Add HCO')
						: entityType === EntityType.Hcp
							? t('HCP Data Change Request')
							: t('HCO Data Change Request')}
				</Typography>

				<Box sx={sx.titleActions}>
					{currentStep === 'SUCCESS' && !pathname.includes(paths.dcrs) && (
						<Button
							sx={sx.dcrLink}
							variant='text'
							component={Link}
							to={paths.dcrs}
						>
							{t('View my DCRs')}
						</Button>
					)}
					{currentStep === 'FORM' && isSimpleView && !loading && (
						<Button
							sx={sx.allFieldsLink}
							variant='text'
							size='small'
							onClick={() => setIsSimpleView(false)}
						>
							{t('View all fields')}
						</Button>
					)}
					<IconButton
						onClick={handleClose}
						aria-label='Close dialog'
						sx={sx.closeIcon}
					>
						<CloseIcon fontSize='small' />
					</IconButton>
				</Box>
			</Box>
			{isMd &&
				!allTabsHidden &&
				!loading &&
				currentStep === 'FORM' &&
				!isSimpleView && (
					<NavigationTabs
						value={currentTab}
						tabs={tabs}
						onChange={onTabChange}
						orientation='horizontal'
						customSx={sx.mobileNavTabsCustom}
					/>
				)}
		</Box>
	);

	const showsFormSearch = currentStep === 'FORM' && !loading && !isSimpleView;
	const showsHeaderDivider =
		!loading &&
		!showsFormSearch &&
		(currentStep === 'PICK_COUNTRY' ||
			currentStep === 'REVIEW' ||
			(currentStep === 'FORM' && isSimpleView));

	const headerNode = showsFormSearch ? (
		<FormSearch entityType={entityType} />
	) : undefined;

	const bodyNode = (
		<Box sx={sx.content}>
			{!isMd &&
				!allTabsHidden &&
				!loading &&
				currentStep === 'FORM' &&
				!isSimpleView && (
					<Box sx={sx.navigation}>
						<NavigationTabs
							value={currentTab}
							tabs={tabs}
							onChange={onTabChange}
						/>
					</Box>
				)}

			{loading && <LoadingIndicator customSx={sx.loadingIndicatorCustom} />}

			{allTabsHidden && <NoFormFields />}

			<Box sx={sx.formContent}>
				{!loading && currentStep === 'PICK_COUNTRY' && (
					<PickCountryModal
						entityType={entityType}
						options={countryOptions}
						preferredCountryId={preferredCountryId}
					/>
				)}

				<Box sx={sx.formStep(currentStep === 'FORM' && !loading)}>
					{entityType === EntityType.Hcp ? (
						// If form values are reset, it's supposed to start from clean state
						// We use key to force remount the form content so that any local state is reset as well
						<HcpDcrModalContent key={formGeneration} />
					) : (
						<HcoDcrModalContent key={formGeneration} />
					)}
				</Box>

				{!loading && currentStep === 'REVIEW' && (
					<ReviewModalContent entityType={entityType} dcrType={dcrType} />
				)}

				{!loading && currentStep === 'SUCCESS' && (
					<DcrSuccessModalContent onClose={handleClose} />
				)}
			</Box>
		</Box>
	);

	const footerNode =
		!loading && currentStep !== 'SUCCESS' ? (
			<>
				{currentStep === 'PICK_COUNTRY' && (
					<PickCountryModalActions
						onClose={handleClose}
						onSeeDcrForm={handleCountryPicked}
						isDirty={!!selectedNewCountryId}
						isValid={!!selectedNewCountryId}
					/>
				)}

				{currentStep === 'FORM' && (
					<DcrModalDialogActions
						onClose={handleClose}
						onSeeReview={handleGoToReview}
						isDirty={isFormDirty}
						isValid={isValid}
						isSimpleView={isSimpleView}
						errors={errors}
					/>
				)}

				{currentStep === 'REVIEW' && (
					<ReviewModalDialogActions
						onBack={handleBackToForm}
						onClose={handleClose}
						attachments={attachments}
						addFiles={addFiles}
						removeAttachment={removeAttachment}
					/>
				)}
			</>
		) : undefined;

	return (
		<>
			<FormProvider {...methods}>
				<DcrContext.Provider value={contextValue}>
					<Modal
						open={open}
						onClose={handleClose}
						size='mdlg'
						fullHeight={isFullHeight}
						disableEscapeKeyDown
						hideCloseButton
						onSubmit={methods.handleSubmit(handleSubmit)}
						bodyRef={scrollRef}
						title={titleNode}
						header={headerNode}
						showHeaderDivider={showsHeaderDivider}
						footer={footerNode}
						footerSpacing={currentStep === 'REVIEW'}
						customSx={{
							footer:
								currentStep === 'REVIEW' ? sx.reviewFooterBleed : sx.actions
						}}
					>
						{bodyNode}
					</Modal>
				</DcrContext.Provider>
			</FormProvider>

			<DiscardChangesWarningModal
				open={showUnsavedConfirm}
				handleHideUnsavedDialog={handleHideUnsavedDialog}
				onClose={discardAndClose}
			/>
		</>
	);
};
